import React from 'react';

import type {
  ChartV2LegendMoreItem,
  ChartV2Series,
  ChartV2SeriesType,
} from '../types/chart-v2.types';
import { ChartTypeSelector } from './ChartTypeSelector';
import { ColorPalette } from './ColorPalette';
import {
  AreaIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  BarIcon,
  CandleIcon,
  LineIcon,
} from './Icons';
import styles from './Legend.module.scss';

const seriesTypeIcon: Record<ChartV2SeriesType, React.ReactNode> = {
  line: <LineIcon />,
  area: <AreaIcon />,
  candlestick: <CandleIcon />,
  bar: <BarIcon />,
};

const DEFAULT_SERIES_TYPE_LABEL: Record<ChartV2SeriesType, string> = {
  line: 'Line chart',
  area: 'Area chart',
  candlestick: 'Candlestick chart',
  bar: 'Bar chart',
};

export interface BuildDefaultLegendMenuLabels {
  changeColor?: string;
  moveUp?: string;
  moveDown?: string;
  seriesTypeLabel?: Partial<Record<ChartV2SeriesType, string>>;
}

export interface BuildDefaultLegendMenuArgs {
  seriesId: string;
  color: string;
  seriesType: ChartV2SeriesType;
  hasOhlc: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  colors: string[];
  onColorChange: (color: string) => void;
  onSeriesTypeChange: (seriesType: ChartV2SeriesType) => void;
  onMovePane?: (direction: 'up' | 'down') => void;
  labels?: BuildDefaultLegendMenuLabels;
}

export const buildDefaultLegendMenu = ({
  seriesType,
  color,
  hasOhlc,
  canMoveUp,
  canMoveDown,
  colors,
  onColorChange,
  onSeriesTypeChange,
  onMovePane,
  labels,
}: BuildDefaultLegendMenuArgs): ChartV2LegendMoreItem[] => {
  const items: ChartV2LegendMoreItem[] = [
    {
      id: 'change-color',
      label: labels?.changeColor ?? 'Change chart color',
      leadingIcon: (
        <div className={styles.colorDot} style={{ backgroundColor: color }} />
      ),
      submenu: (
        <ColorPalette
          colors={colors}
          selectedColor={color}
          onSelect={onColorChange}
        />
      ),
    },
  ];

  // The chart-type switcher only offers OHLC styles (candlestick / bar) on
  // top of line. With no OHLC data there's nothing to switch to, so we hide
  // the chart-type item entirely instead of showing disabled rows.
  if (hasOhlc) {
    const resolvedSeriesTypeLabel =
      labels?.seriesTypeLabel?.[seriesType] ??
      DEFAULT_SERIES_TYPE_LABEL[seriesType];

    items.push({
      id: 'chart-type',
      label: resolvedSeriesTypeLabel,
      leadingIcon: seriesTypeIcon[seriesType],
      submenu: (
        <ChartTypeSelector
          selected={seriesType}
          onChange={onSeriesTypeChange}
        />
      ),
    });
  }

  items.push(
    {
      id: 'move-up',
      label: labels?.moveUp ?? 'Move up',
      leadingIcon: <ArrowUpIcon />,
      disabled: !canMoveUp,
      onClick: () => onMovePane?.('up'),
    },
    {
      id: 'move-down',
      label: labels?.moveDown ?? 'Move down',
      leadingIcon: <ArrowDownIcon />,
      disabled: !canMoveDown,
      onClick: () => onMovePane?.('down'),
    }
  );

  return items;
};

export const seriesHasOhlc = (series: ChartV2Series): boolean =>
  series.data.some(p => 'close' in p);
